import type { SelectOption } from '@scaleaq/scaleui';

import { BiomassDistributionRequest } from '@scaleaq/sitecontrol-lib';
import { DateTime } from 'luxon';

import { formatLocalePercent, round, toApiCalendarDay, toApiDate } from '@/utils';

import {
  BiomassDistributionBinSize,
  BiomassDistributionInterval,
  intervalToDuration,
} from './biomassDistribution.types.ts';

/** Number of samples used to draw the estimated distribution curve. */
const BELL_CURVE_POINT_COUNT = 61;
/** The curve spans mean ± this many standard deviations (highcharts' bellcurve default). */
const BELL_CURVE_SIGMA_SPAN = 3;
/**
 * A skew-normal distribution can only represent |skewness| < ~0.995 (as its shape parameter goes to
 * infinity). Stronger sample skewness is clamped to this, keeping the curve well defined.
 */
const MAX_SKEW_NORMAL_SKEWNESS = 0.99;
/**
 * Bins holding less than this share of all measurements (0.1 %) round to 0 % in the chart, so they
 * are left out of the histogram instead of drawing invisible slivers along the axis.
 */
const MIN_PLOTTED_BIN_SHARE = 0.001;
/** |skewness| below this is too slight to be visible in the curve, and reads as symmetric. */
const SYMMETRIC_SKEWNESS_LIMIT = 0.1;
/** Decimals used for the statistics shown in the estimated distribution tooltip. */
const DISTRIBUTION_STATISTIC_DECIMALS = 2;
/**
 * Rough width of one character of an interval label at the 12 px axis font, and the blank space
 * kept between two neighbouring labels. Only used to count how many labels fit the axis, so an
 * over-estimate costs at most one label rather than causing an overlap.
 */
const INTERVAL_LABEL_CHARACTER_WIDTH = 7;
const INTERVAL_LABEL_SPACING = 12;

export const getPercentageString = (
  dataLength: number,
  count: number | undefined,
  language: string
) =>
  count !== undefined && dataLength
    ? formatLocalePercent(language, count / dataLength, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })
    : '';

export const roundAndPad = (
  value: number,
  binSize: BiomassDistributionBinSize,
  language: string
) => {
  // ensure small and medium bins always display exactly one decimal, locale-formatted
  const digits = binSize === BiomassDistributionBinSize.Large ? 0 : 1;
  return new Intl.NumberFormat(language, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(round(value, 1) ?? value);
};

/** Bin width in kilograms, the unit shared by the UI and the distribution endpoint. */
export const getBinSizeValue = (binSize: BiomassDistributionBinSize) => {
  switch (binSize) {
    case BiomassDistributionBinSize.Large:
      return 1;
    case BiomassDistributionBinSize.Medium:
      return 0.5;
    case BiomassDistributionBinSize.SmallMedium:
      return 0.2;
    case BiomassDistributionBinSize.Small:
      return 0.1;
  }
};

/**
 * Center of the bin starting at `lowerBound`, in kg. Highcharts draws a column centered on its x
 * value, so plotting a bin at its center is what makes the column cover exactly the interval it
 * stands for, between the boundaries its own label names.
 */
export const getBinCenter = (lowerBound: number, binSizeValue: number) =>
  lowerBound + binSizeValue / 2;

/** The weight interval covered by the bin plotted at `binCenter`, for example `0.5 - 1.0`. */
export const formatBinInterval = (
  binCenter: number,
  binSize: BiomassDistributionBinSize,
  language: string
) => {
  const halfBin = getBinSizeValue(binSize) / 2;
  return `${roundAndPad(binCenter - halfBin, binSize, language)} - ${roundAndPad(binCenter + halfBin, binSize, language)}`;
};

/** Horizontal room one interval label needs on the axis, the gap to its neighbour included. */
export const getIntervalLabelWidth = (labels: string[]) =>
  labels.reduce((longest, label) => Math.max(longest, label.length), 0) *
    INTERVAL_LABEL_CHARACTER_WIDTH +
  INTERVAL_LABEL_SPACING;

interface IntervalTickPositionsOptions {
  /** Centers of the plotted bins, in kg, ascending. */
  binCenters: number[];
  /** Current axis extremes, in kg. */
  min: number;
  max: number;
  /** Length of the axis in pixels. */
  axisLength: number;
  /** Room one label takes up, from {@link getIntervalLabelWidth}. */
  labelWidth: number;
}

/**
 * Tick positions for the biomass axis: bin centers only, keeping every n-th one so the labels fit
 * the axis. Highcharts' own placement picks round numbers that ignore the bin size, which drops
 * labels on a column border and names intervals no column covers — and since the interval it
 * picks depends on the pixel width, the labels shift about as the widget is resized or zoomed.
 * Deriving the positions from the bins instead pins each label to the middle of its own column at
 * any size, as this runs again on every redraw.
 */
export const getIntervalTickPositions = ({
  binCenters,
  min,
  max,
  axisLength,
  labelWidth,
}: IntervalTickPositionsOptions): number[] => {
  const visibleCenters = binCenters.filter(center => center >= min && center <= max);
  if (visibleCenters.length === 0) return [];

  const labelsThatFit = Math.max(1, Math.floor(axisLength / labelWidth));
  const binsPerLabel = Math.ceil(visibleCenters.length / labelsThatFit);

  return visibleCenters.filter((_, index) => index % binsPerLabel === 0);
};

export const buildEnumSelectOptions = <E extends Record<string, string>>(
  enumObject: E,
  getLabel: (key: keyof E & string) => string
): SelectOption<E[keyof E]>[] =>
  (Object.keys(enumObject) as Array<keyof E & string>).map(key => ({
    label: getLabel(key),
    value: enumObject[key],
  }));

/**
 * Query for the biomass distribution endpoint (bin size in kg). The window ends at today's date in
 * Oslo, which can differ from the user's own date (in San Francisco in the evening it is already
 * tomorrow in Oslo), and both bounds are sent as midnight in Oslo. The request stays stable until
 * Oslo midnight and covers whole Oslo days.
 */
export const getBiomassDistributionRequest = (
  interval: BiomassDistributionInterval,
  binSize: BiomassDistributionBinSize
): BiomassDistributionRequest => {
  const end = toApiCalendarDay(DateTime.now());
  const start = end.minus(intervalToDuration[interval]).startOf('day');
  return {
    start: toApiDate(start),
    end: toApiDate(end),
    binSize: getBinSizeValue(binSize),
  };
};

/** Which way the fitted curve leans, used to explain the skewness in the chart tooltip. */
export type SkewnessLean = 'symmetric' | 'towardsHeavier' | 'towardsLighter';

/**
 * Describes the lean of a curve drawn with the given {@link getPlottedSkewness} value: a positive
 * skewness draws a long tail towards heavier fish, a negative one towards lighter fish, and 0
 * leaves the curve symmetric.
 */
export const getSkewnessLean = (skewness: number): SkewnessLean => {
  if (!Number.isFinite(skewness) || skewness === 0) return 'symmetric';
  return skewness > 0 ? 'towardsHeavier' : 'towardsLighter';
};

/**
 * Skewness used to draw the fitted curve and describe it in the tooltip. Values within
 * {@link SYMMETRIC_SKEWNESS_LIMIT} of 0 are treated as symmetric; stronger skewness is clamped to
 * what a skew-normal can represent.
 */
export const getPlottedSkewness = (skewness: number) => {
  if (!Number.isFinite(skewness) || Math.abs(skewness) < SYMMETRIC_SKEWNESS_LIMIT) return 0;
  return Math.max(-MAX_SKEW_NORMAL_SKEWNESS, Math.min(MAX_SKEW_NORMAL_SKEWNESS, skewness));
};

/** Locale-formatted statistic (mean, standard deviation, skewness) for the chart tooltip. */
export const formatDistributionStatistic = (value: number, language: string) =>
  new Intl.NumberFormat(language, {
    minimumFractionDigits: DISTRIBUTION_STATISTIC_DECIMALS,
    maximumFractionDigits: DISTRIBUTION_STATISTIC_DECIMALS,
  }).format(value);

/**
 * Whether a bin holds enough measurements to be worth drawing as a column. Bins under
 * {@link MIN_PLOTTED_BIN_SHARE} of the total count are dropped from the histogram.
 */
export const isPlottedBin = (count: number, totalCount: number) =>
  totalCount > 0 ? count / totalCount >= MIN_PLOTTED_BIN_SHARE : count > 0;

/**
 * Samples the estimated probability density function between mean ± 3σ. Replaces the highcharts
 * bell curve series, which needed every raw measurement as its base series.
 *
 * With plotted skewness at 0 this is the plain normal density. Otherwise it fits a skew-normal
 * that keeps the observed mean and standard deviation and uses {@link getPlottedSkewness} so the
 * curve leans the same way as the measured biomass distribution.
 */
export const getSkewNormalCurvePoints = (
  mean: number,
  standardDeviation: number,
  skewness = 0
): [number, number][] => {
  if (standardDeviation <= 0) return [];

  const { location, scale, shape } = getSkewNormalParameters(mean, standardDeviation, skewness);

  const start = mean - BELL_CURVE_SIGMA_SPAN * standardDeviation;
  const step = (2 * BELL_CURVE_SIGMA_SPAN * standardDeviation) / (BELL_CURVE_POINT_COUNT - 1);

  return Array.from({ length: BELL_CURVE_POINT_COUNT }, (_, index) => {
    const x = start + index * step;
    const z = (x - location) / scale;
    // Azzalini skew-normal density 2/ω·φ(z)·Φ(αz), which collapses to the normal pdf when α is 0.
    return [x, (2 * standardNormalDensity(z) * standardNormalCumulative(shape * z)) / scale];
  });
};

// Helper functions

/**
 * Solves the skew-normal location (ξ), scale (ω) and shape (α) that reproduce the given mean,
 * standard deviation and skewness, using the standard moment relations
 * δ = sign(γ)·√(π/2·|γ|^(2/3) / (|γ|^(2/3) + ((4−π)/2)^(2/3))), α = δ/√(1−δ²),
 * ω = σ/√(1−2δ²/π) and ξ = μ − ωδ√(2/π).
 */
const getSkewNormalParameters = (mean: number, standardDeviation: number, skewness: number) => {
  const usableSkewness = getPlottedSkewness(skewness);

  if (usableSkewness === 0) return { location: mean, scale: standardDeviation, shape: 0 };

  const skewnessTerm = Math.abs(usableSkewness) ** (2 / 3);
  const delta =
    Math.sign(usableSkewness) *
    Math.sqrt(((Math.PI / 2) * skewnessTerm) / (skewnessTerm + ((4 - Math.PI) / 2) ** (2 / 3)));

  const scale = standardDeviation / Math.sqrt(1 - (2 * delta ** 2) / Math.PI);

  return {
    location: mean - scale * delta * Math.sqrt(2 / Math.PI),
    scale,
    shape: delta / Math.sqrt(1 - delta ** 2),
  };
};

/** Standard normal probability density φ(z). */
const standardNormalDensity = (z: number) => Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);

/** Standard normal cumulative distribution Φ(z), via the error function below. */
const standardNormalCumulative = (z: number) => 0.5 * (1 + errorFunction(z / Math.SQRT2));

/**
 * Error function erf(x), Abramowitz & Stegun 7.1.26 (absolute error below 1.5e-7) — plenty for
 * drawing a curve, and it avoids pulling in a numeric dependency.
 */
const errorFunction = (x: number) => {
  const t = 1 / (1 + 0.3275911 * Math.abs(x));
  const series =
    t *
    (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))));
  return Math.sign(x) * (1 - series * Math.exp(-x * x));
};
